import React, { Suspense, lazy, useEffect } from 'react'
import { render } from 'react-dom'
import { BrowserRouter as Router, Switch, Route, Redirect, Link, NavLink } from 'react-router-dom'
import { Provider } from 'react-redux'
import store from './store'
import SuspenseLoader from './components/suspense_loader/SuspenseLoader'

import useUser from 'context/UserContext'

import ScrollToTop from './navigation/ScrollToTop'
import RouteListener from './navigation/RouteListener'
import { getParameterByName } from 'utils/js/browser_helpers'
import {
    eas_authorization_path,
    eas_client_id,
    eas_redirect_url,
    eas_secure_domain,
    sn_url,
} from 'utils/js/endpoints'

import './utils/scss/global.scss'
import { SharedContextProvider } from './context/sharedContext'
import useConsent from './context/ConsentContext'
import loadjs from 'loadjs'

const Standings = lazy(() => import('./containers/standings/Standings'))
const Players = lazy(() => import('./containers/players/Players'))
const Search = lazy(() => import('./containers/search/Search'))
const Stats = lazy(() => import('./containers/stats/Stats'))
const Videos = lazy(() => import('./containers/videos/Videos'))
const Livetrackers = lazy(() => import('./containers/livetrackers/Livetrackers'))
const Scoreboard = lazy(() => import('./containers/scoreboard/Scoreboard'))
const Scores = lazy(() => import('./containers/scores/Scores'))
const PlayerProfile = lazy(() => import('./containers/player_profile/PlayerProfile'))
const AncillarySport = lazy(() => import('./containers/ancillary_sport/AncillarySport'))
const AncillaryLeague = lazy(() => import('./containers/ancillary_league/AncillaryLeague'))
const HockeyTournament = lazy(() => import('./containers/tournaments/hockey/HockeyTournament'))
const SoccerTournament = lazy(() => import('./containers/tournaments/soccer/SoccerTournament'))
const Schedule = lazy(() => import('./containers/schedule/Schedule'))
const PlayoffTracker = lazy(() => import('./containers/playoff_tracker/PlayoffTracker'))
const NotFoundGeneral = lazy(() => import('./components/NotFoundGeneral'))
const TradeSigningsTracker = lazy(() =>
    import('./containers/trade_signings_tracker/TradeSigningsTracker')
)
const DraftTracker = lazy(() => import('./containers/draft_tracker/DraftTracker'))
const MedalTracker = lazy(() => import('./containers/medal_tracker/MedalTracker'))
const Gamecode = lazy(() => import('./containers/gamecode/Gamecode'))
const BasketballTournament = lazy(() =>
    import('./containers/tournaments/basketball/BasketballTournament')
)
const LacrosseTournament = lazy(() =>
    import('./containers/tournaments/lacrosse/LacrosseTournament')
)
const BaseballTournament = lazy(() =>
    import('./containers/tournaments/baseball/BaseballTournament')
)
const LazyGlobalFooter = lazy(() =>
    import('@digitalmedia/sn-design-system').then(module => ({ default: module.GlobalFooter }))
)
const LazyNavBar = lazy(() =>
    import('@digitalmedia/sn-design-system').then(module => ({ default: module.NavBar }))
)
const LazyConsentModal = lazy(() =>
    import('@digitalmedia/sn-design-system').then(module => ({ default: module.ConsentModal }))
)
const LazyAlertBanner = lazy(() =>
    import('@digitalmedia/sn-design-system').then(module => ({ default: module.AlertBanner }))
)

const HockeyStandings = lazy(() => import('./components/standings_tables/hockey/HockeyStandings'))

// Adding commit to force prod deploy
const Nav = () => {
    const { user } = useUser()

    const isBasicTemplate = getParameterByName('template') === 'basic'
    const navWithProps = {
        link: Link,
        navlink: NavLink,
    }

    isBasicTemplate
        ? document.getElementsByTagName('html')[0].classList.add('appDarkMode')
        : document.getElementsByTagName('html')[0].classList.remove('appDarkMode')

    return (
        !isBasicTemplate && (
            <Suspense fallback={null}>
                <LazyNavBar
                    withNavProps={navWithProps}
                    displayName={user.displayName}
                    isUserLoggedIn={user.isLoggedIn}
                    authorizePath={eas_authorization_path}
                    clientId={eas_client_id}
                    redirectUrl={eas_redirect_url}
                    secureDomain={eas_secure_domain}
                    watchLiveUrl="https://watch.sportsnet.ca/"
                    firstName={user.firstName}
                    snUrl={`${sn_url}/`}
                    darkModeEnabledPaths={[
                        '/hockey/nhl/games/:gamecode',
                        '/basketball/nba/games/:gamecode',
                        '/basketball/wnba/games/:gamecode',
                        '/basketball/ncaamb/games/:gamecode',
                        '/baseball/mlb/games/:gamecode',
                        '/football/nfl/games/:gamecode',
                        '/football/cfl/games/:gamecode',
                        '/football/ncaafb/games/:gamecode',
                        '/hockey/nhl/trade-tracker',
                        '/hockey/nhl/signings',
                    ]}
                />
            </Suspense>
        )
    )
}

//Consent Modal
const ConsentModal = () => {
    const isBasicTemplate = getParameterByName('template') === 'basic'
    const regionCookieEnabled = window !== undefined && document.cookie.includes('rsm_user_consent')
    const { hideConsentModal, setHideConsentModal, setShowFooterConsentLink } = useConsent()

    useEffect(() => {
        if (!document.cookie.includes('rsm_user_consent')) {
            setHideConsentModal(true)
            setShowFooterConsentLink(false)
        } else {
            if (window !== undefined && window.localStorage.getItem('rsm_consent_config')) {
                const rogersConsentObject = window.localStorage.getItem('rsm_consent_config')
                const rogersConsent = JSON.parse(rogersConsentObject)

                if (rogersConsent) {
                    window.rogers = {
                        consent: rogersConsent,
                    }
                }

                setHideConsentModal(true)
                setShowFooterConsentLink(true)
            } else if (window !== undefined && !window.localStorage.getItem('rsm_consent_config')) {
                setHideConsentModal(false)
                setShowFooterConsentLink(false)
            }
        }
    }, [])

    const callbackConsentValue = consent => {
        setHideConsentModal(true)
        setShowFooterConsentLink(true)
        const userConsent = consent()
        const functionalCookies = [
            'https://ovp.iris.tv/plugins/brightcove/v2/styles/iris-bc.adaptive.min.css',
            'https://ovp.iris.tv/libs/adaptive/v2/iris.adaptive.js',
        ]

        if (
            process.env.REACT_APP_ENV === 'integration' ||
            process.env.REACT_APP_ENV === 'staging'
        ) {
            functionalCookies.push('https://staging-utility.rogersmedia.com/utilityx.js')
        } else {
            functionalCookies.push('https://8c11ebd904100d.rogersmedia.com/utilityx.js')
        }

        if (userConsent?.functional) {
            if (!loadjs.isDefined('functionalCookiesScript')) {
                loadjs(functionalCookies, 'functionalCookiesScript', { async: false })
            }
        }
    }

    return (
        !isBasicTemplate &&
        regionCookieEnabled && (
            <Suspense fallback={null}>
                <LazyConsentModal
                    hide={hideConsentModal}
                    callbackConsentValue={callbackConsentValue}
                />
            </Suspense>
        )
    )
}

const Footer = () => {
    const { showFooterConsentLink, setHideConsentModal, setShowFooterConsentLink } = useConsent()
    const isBasicTemplate = getParameterByName('template') === 'basic'
    const onConsentModal = () => {
        setHideConsentModal(false)
        setShowFooterConsentLink(false)
    }
    return (
        <Suspense fallback={null}>
            {!isBasicTemplate && (
                <LazyGlobalFooter
                    consentLink={showFooterConsentLink}
                    onConsentModal={onConsentModal}
                />
            )}
        </Suspense>
    )
}

const Banner = () => {
    return (
        <Suspense fallback={null}>
            <LazyAlertBanner />
        </Suspense>
    )
}

const router = (
    <Provider store={store}>
        <SharedContextProvider>
            <Router>
                <ScrollToTop>
                    <RouteListener>
                        <Route path="/:sport?/:league?" component={Nav} />
                        <Route path="" component={Banner} />
                        <Suspense fallback={<SuspenseLoader />}>
                            <Switch>
                                <Route exact path="/:sport/:league/stats" component={Stats} />
                                <Route exact path="/:sport/:league/players" component={Players} />

                                <Route
                                    exact
                                    path="/basketball/wnba/scores/:day?"
                                    component={props => (
                                        <BasketballTournament
                                            sport="basketball"
                                            league="wnba"
                                            {...props}
                                        />
                                    )}
                                />
                                <Route
                                    exact
                                    path="/juniors/memorial-cup/standings"
                                    component={props => <HockeyStandings league="chl" {...props} />}
                                />
                                <Route
                                    exact
                                    path="/:sport/:league/standings"
                                    component={Standings}
                                />
                                <Route
                                    exact
                                    path="/:sport/:league/playoffs"
                                    component={PlayoffTracker}
                                />
                                <Route
                                    exact
                                    path="/:sport/:league/:page(trade-tracker|signings)"
                                    component={TradeSigningsTracker}
                                />
                                <Route
                                    exact
                                    path="/:sport/:league/draft-tracker"
                                    component={DraftTracker}
                                />
                                <Route
                                    exact
                                    path="/:sport/:league/games/:gamecode"
                                    component={Livetrackers}
                                />
                                <Route
                                    exact
                                    path="/:sport/:league/players/:player_name/:player_id"
                                    component={PlayerProfile}
                                />

                                <Redirect
                                    exact
                                    from="/:sport(golf|ufc|tennis|auto-racing)/:league/"
                                    to="/:sport(golf|ufc|tennis|auto-racing)/:league/schedule"
                                />
                                <Route
                                    exact
                                    path="/:sport(golf|ufc|tennis|auto-racing)/:league/:page(schedule|results|rankings|events)/:id?"
                                    component={AncillaryLeague}
                                />

                                <Route
                                    exact
                                    path="/hockey/4nationsfaceoff/scores/:day?"
                                    component={props => (
                                        <HockeyTournament
                                            sport="hockey"
                                            league="4nationsfaceoff"
                                            {...props}
                                        />
                                    )}
                                />

                                <Route
                                    exact
                                    path="/:sport/:league/scores/:day?"
                                    component={Scoreboard}
                                />
                                <Route
                                    exact
                                    path="/videos/:sport?/:league?/:slug?"
                                    component={Videos}
                                />
                                <Route
                                    exact
                                    path="/soccer/euro/:day?"
                                    component={props => (
                                        <SoccerTournament sport="soccer" league="euro" {...props} />
                                    )}
                                />
                                <Route
                                    exact
                                    path="/soccer/world-cup-2022/:day?"
                                    component={props => (
                                        <SoccerTournament sport="soccer" league="natl" {...props} />
                                    )}
                                />
                                <Route
                                    exact
                                    path="/soccer/womens-world-cup/:day?"
                                    component={props => (
                                        <SoccerTournament sport="soccer" league="wwc" {...props} />
                                    )}
                                />
                                <Route
                                    exact
                                    path="/olympics/basketball/:page/:year?"
                                    component={props => (
                                        <BasketballTournament
                                            sport="basketball"
                                            league="oly"
                                            {...props}
                                        />
                                    )}
                                />
                                <Redirect
                                    exact
                                    from="/olympics/basketball"
                                    to="/olympics/basketball/womens/2020"
                                />
                                <Route
                                    exact
                                    path="/olympics/hockey/:page/:year?"
                                    component={props => (
                                        <HockeyTournament sport="hockey" league="oly" {...props} />
                                    )}
                                />
                                <Redirect
                                    exact
                                    from="/olympics/hockey"
                                    to="/olympics/hockey/womens/2022"
                                />
                                <Route
                                    exact
                                    path="/olympics/medal-table/:year"
                                    component={props => <MedalTracker league="oly" {...props} />}
                                />
                                <Redirect
                                    exact
                                    from="/olympics/medal-table"
                                    to="/olympics/medal-table/2024/"
                                />
                                <Route
                                    exact
                                    path="/olympics/soccer/:page/:year?"
                                    component={props => (
                                        <SoccerTournament sport="soccer" league="oly" {...props} />
                                    )}
                                />
                                <Redirect
                                    exact
                                    from="/olympics/soccer"
                                    to="/olympics/soccer/womens/2020"
                                />
                                <Route
                                    exact
                                    path="/soccer/:leagueOrSubPage/:dayOrSubPage?"
                                    component={props => (
                                        <AncillarySport sport="soccer" {...props} />
                                    )}
                                />
                                <Route
                                    exact
                                    path="/juniors/memorial-cup/:day?"
                                    component={props => (
                                        <HockeyTournament sport="juniors" league="chl" {...props} />
                                    )}
                                />

                                <Route
                                    exact
                                    path="/juniors/world-juniors/:day?"
                                    component={props => (
                                        <HockeyTournament sport="hockey" league="wjc" {...props} />
                                    )}
                                />
                                <Route
                                    exact
                                    path="/hockey/wwhc/:day?"
                                    component={props => (
                                        <HockeyTournament sport="hockey" league="wwhc" {...props} />
                                    )}
                                />
                                <Route
                                    exact
                                    path="/hockey/whc/:day?"
                                    component={props => (
                                        <HockeyTournament sport="hockey" league="whc" {...props} />
                                    )}
                                />

                                <Route
                                    exact
                                    path="/juniors/:leagueOrSubPage/:dayOrSubPage?"
                                    component={props => (
                                        <AncillarySport sport="juniors" {...props} />
                                    )}
                                />
                                <Route
                                    exact
                                    path="/baseball/wbc/:day?"
                                    component={props => (
                                        <BaseballTournament
                                            sport="baseball"
                                            league="wbc"
                                            {...props}
                                        />
                                    )}
                                />

                                <Route
                                    exact
                                    path="/basketball/wnba/:day?"
                                    component={props => (
                                        <BasketballTournament
                                            sport="basketball"
                                            league="wnba"
                                            {...props}
                                        />
                                    )}
                                />

                                <Route
                                    exact
                                    path="/lacrosse/nll/:day?"
                                    component={props => (
                                        <LacrosseTournament
                                            sport="lacrosse"
                                            league="nll"
                                            {...props}
                                        />
                                    )}
                                />
                                <Route
                                    exact
                                    path="/scores/:day?"
                                    component={props => <Scores {...props} />}
                                />
                                <Route exact path="/schedule/:day?" component={Schedule} />
                                <Route exact path="/search/:keyword?" component={Search} />
                                <Route exact path="/gamecode" component={Gamecode} />
                                <Route component={NotFoundGeneral} />
                            </Switch>
                        </Suspense>
                        <Route component={ConsentModal} />
                        <Route path="/:sport?/:league?" component={Footer} />
                    </RouteListener>
                </ScrollToTop>
            </Router>
        </SharedContextProvider>
    </Provider>
)

render(router, document.getElementById('root'))
